import { useState, useEffect, useRef } from 'react';

function useScrollReveal(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('animate-in');
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
}

export default function ContactBooking() {
  const headerRef = useScrollReveal();
  const optionsRef = useScrollReveal();
  const formRef = useScrollReveal(0.05);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => formBody.append(key, value));
      const response = await fetch('https://readdy.ai/api/form/d56ogrmisj01hmefqfrg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', date: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-sand-100 relative overflow-hidden">
      <style>{`
        .cb-reveal { opacity: 0; transform: translateY(32px); transition: opacity 0.75s ease, transform 0.75s ease; }
        .cb-reveal.animate-in { opacity: 1; transform: translateY(0); }
        .cb-reveal-delay { opacity: 0; transform: translateY(32px); transition: opacity 0.75s ease 0.2s, transform 0.75s ease 0.2s; }
        .cb-reveal-delay.animate-in { opacity: 1; transform: translateY(0); }
        .cb-reveal-delay2 { opacity: 0; transform: translateY(32px); transition: opacity 0.75s ease 0.1s, transform 0.75s ease 0.1s; }
        .cb-reveal-delay2.animate-in { opacity: 1; transform: translateY(0); }
      `}</style>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div ref={headerRef} className="cb-reveal text-center mb-16">
          <p className="text-xs font-bold tracking-widest text-teal-600 uppercase mb-3">Réservation</p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Prêt à rider les vagues ?
          </h2>
          <p className="text-base text-gray-500 max-w-xl mx-auto">
            Réservez votre cours ou votre équipement dès aujourd'hui. Faisons de votre rêve surf une réalité !
          </p>
        </div>

        {/* Booking Options */}
        <div ref={optionsRef} className="cb-reveal-delay2 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-16">
          <div className="bg-sand-50 border border-sand-200 rounded-2xl p-8 hover:border-teal-400 hover:shadow-md transition-all cursor-pointer group">
            <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-xl mb-4 group-hover:bg-teal-500 transition-all">
              <i className="ri-surfing-line text-2xl text-teal-600 group-hover:text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Réserver un cours</h3>
            <p className="text-sm text-gray-500 mb-4">Commencez votre aventure surf ou kitesurf avec nos instructeurs certifiés</p>
            <div className="flex items-center text-teal-600 font-semibold text-sm whitespace-nowrap">
              En savoir plus <i className="ri-arrow-right-line ml-2"></i>
            </div>
          </div>
          <div className="bg-sand-50 border border-sand-200 rounded-2xl p-8 hover:border-orange-400 hover:shadow-md transition-all cursor-pointer group">
            <div className="w-14 h-14 flex items-center justify-center bg-orange-100 rounded-xl mb-4 group-hover:bg-orange-500 transition-all">
              <i className="ri-shopping-bag-3-line text-2xl text-orange-600 group-hover:text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Louer du matériel</h3>
            <p className="text-sm text-gray-500 mb-4">Équipement premium pour les riders expérimentés et les aventuriers</p>
            <div className="flex items-center text-orange-600 font-semibold text-sm whitespace-nowrap">
              Voir le catalogue <i className="ri-arrow-right-line ml-2"></i>
            </div>
          </div>
        </div>

        {/* Contact Form & Info */}
        <div ref={formRef} className="cb-reveal grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-sm border border-sand-200 p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Envoyez-nous un message</h3>
            <form id="contact-booking-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Nom complet *</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border border-sand-200 bg-sand-50 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none transition-all text-sm" placeholder="Votre nom" />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Email *</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border border-sand-200 bg-sand-50 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none transition-all text-sm" placeholder="votre@email.com" />
              </div>
              <div>
                <label htmlFor="phone" className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Téléphone</label>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border border-sand-200 bg-sand-50 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none transition-all text-sm" placeholder="+212 XXX XXX XXX" />
              </div>
              <div>
                <label htmlFor="service" className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Service *</label>
                <select id="service" name="service" value={formData.service} onChange={handleChange} required className="w-full px-4 py-3 border border-sand-200 bg-sand-50 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none transition-all text-sm cursor-pointer">
                  <option value="">Choisir un service</option>
                  <option value="Surf Lessons">Cours de Surf</option>
                  <option value="Kitesurf Lessons">Cours de Kitesurf</option>
                  <option value="Private Sessions">Sessions Privées</option>
                  <option value="Equipment Rental">Location de Matériel</option>
                </select>
              </div>
              <div>
                <label htmlFor="date" className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Date souhaitée</label>
                <input type="date" id="date" name="date" value={formData.date} onChange={handleChange} className="w-full px-4 py-3 border border-sand-200 bg-sand-50 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none transition-all text-sm cursor-pointer" />
              </div>
              <div>
                <label htmlFor="message" className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Message</label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} maxLength={500} rows={4} className="w-full px-4 py-3 border border-sand-200 bg-sand-50 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none transition-all resize-none text-sm" placeholder="Parlez-nous de votre niveau et de ce que vous souhaitez apprendre..." />
                <p className="text-xs text-gray-400 mt-1">{formData.message.length}/500 caractères</p>
              </div>
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-checkbox-circle-fill mr-2"></i>Message envoyé ! Nous vous répondrons très vite.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-error-warning-fill mr-2"></i>Une erreur s'est produite. Veuillez réessayer.
                </div>
              )}
              <button type="submit" disabled={isSubmitting} className="w-full bg-gray-900 text-white py-4 rounded-full font-semibold text-sm whitespace-nowrap hover:bg-teal-600 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
                {isSubmitting ? 'Envoi...' : 'Envoyer le message'}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Contactez-nous</h3>
              <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                Des questions ? Nous sommes là ! Contactez-nous via WhatsApp, email ou venez nous voir directement sur la plage d'Essaouira.
              </p>
            </div>
            <div className="space-y-3">
              <a href="https://wa.me/212600000000" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 bg-white border border-sand-200 rounded-xl p-5 hover:border-green-300 hover:shadow-sm transition-all cursor-pointer group">
                <div className="w-11 h-11 flex items-center justify-center bg-green-500 rounded-xl flex-shrink-0">
                  <i className="ri-whatsapp-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">WhatsApp</p>
                  <p className="text-sm text-gray-400">+212 600 000 000</p>
                </div>
                <i className="ri-arrow-right-line text-lg text-gray-300 ml-auto group-hover:text-green-500 transition-colors"></i>
              </a>
              <a href="mailto:info@destination-essaouira.com" className="flex items-center gap-4 bg-white border border-sand-200 rounded-xl p-5 hover:border-teal-300 hover:shadow-sm transition-all cursor-pointer group">
                <div className="w-11 h-11 flex items-center justify-center bg-teal-500 rounded-xl flex-shrink-0">
                  <i className="ri-mail-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Email</p>
                  <p className="text-sm text-gray-400">info@destination-essaouira.com</p>
                </div>
                <i className="ri-arrow-right-line text-lg text-gray-300 ml-auto group-hover:text-teal-500 transition-colors"></i>
              </a>
              <div className="flex items-center gap-4 bg-white border border-sand-200 rounded-xl p-5">
                <div className="w-11 h-11 flex items-center justify-center bg-orange-500 rounded-xl flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Localisation</p>
                  <p className="text-sm text-gray-400">Plage d'Essaouira, Maroc</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-sm border border-sand-200 h-64 w-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d54321.12345678901!2d-9.7689!3d31.5085!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xdad9a4e9f588ccf%3A0x57421a176d5d7d0a!2sEssaouira%2C%20Morocco!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Essaouira Location"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
